import React, {
  useMemo,
  useCallback,
  useState,
  useRef,
  useEffect
} from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Grid,
  Typography
} from '@material-ui/core/';
import { makeStyles } from '@material-ui/core/styles';
import {
  LinearProgress,
  useGetList,
  useGetOne,
  useNotify,
  useRefresh,
  SaveButton,
  Datagrid,
  SelectInput,
  FunctionField,
  ListContextProvider,
  ListView
} from 'react-admin';
import { useForm, useFormState } from 'react-final-form';

import DialogTitleWithCloseButton from '../../../core/components/DialogTitleWithCloseButton';
import useNotifyServerError from '../../../core/hooks/useNotifyServerError';
import InvestorApi from '../../../core/apis/InvestorApi';
import SimpleFormWithSave from '../../../core/components/form/SimpleFormWithSave';
import useListController from '../../../core/components/forked/list/useListController';

const useStyles = makeStyles({
  selectContainer: {
    margin: 0,
    '& > *:only-child': {
      border: 'none'
    }
  },
  row: {
    backgroundColor: 'white !important'
  },
  table: {
    width: '100%'
  },
  progress: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '25px',
    minHeight: '50px'
  },
  noshow: {
    display: 'none !important'
  },
  dialog: {
    minHeight: '75px'
  }
});

const selectOptions = { label: null, helperText: null };
const USER_ID = ':userId';
const DOCUMENT_TYPE_ID = ':documentTypeId';

const PrimaryContacts = ({
  investorId,
  formRef,
  setDisableSubmit,
  setSubmitting,
  onSubmitEnd,
  handleDialogToggle
}) => {
  const requestProps = useMemo(
    () => ({
      basePath: '/primarycontacts',
      resource: 'primarycontacts',
      filter: { investorId: investorId },
      perPage: -1,
      exporter: false,
      bulkActionButtons: false,
      title: ' ', // this is necessary because framework was adding defaultTitle to form header
      pagination: null,
      action: null
    }),
    [investorId]
  );

  const controllerProps = useListController(requestProps);
  const notifyServerError = useNotifyServerError();
  const notify = useNotify();
  const refresh = useRefresh();
  const validData = useRef([{}, {}, {}]);
  const classes = useStyles();

  const {
    data: investor,
    loading: iLoading,
    error: iError,
    loaded: iLoaded
  } = useGetOne('investors', investorId);
  const {
    data: documentTypes,
    loading: dtLoading,
    error: dtError,
    loaded: dtLoaded
  } = useGetList(
    'documenttypes',
    { perPage: -1 },
    { field: 'description', order: 'ASC' }
  );
  const {
    data: contactsData,
    loading: cLoading,
    error: cError,
    loaded: cLoaded
  } = useGetList(
    'investorcontacts',
    { perPage: -1 },
    { field: 'firstName', order: 'ASC' },
    { investorId: investorId }
  );

  const contacts = useMemo(() => {
    return (
      (cLoaded &&
        Object.values(contactsData).map(ct => ({
          id: ct.userId,
          name: `${ct.firstName} ${ct.lastName}`
        }))) ||
      []
    );
  }, [cLoaded, contactsData]);

  const [newControllerProps, initialValues] = useMemo(() => {
    if (!controllerProps.loaded || !iLoaded || !dtLoaded) {
      return validData.current;
    }
    const nIds = [...controllerProps.ids];
    const pcs = { ...controllerProps.data };
    Object.values(documentTypes).forEach(dt => {
      const id = `${investorId}:${dt.id}:P`;
      if (!pcs[id]) {
        pcs[id] = {
          investorId: investorId,
          documentTypeId: dt.id,
          userId: null,
          id: id
        };
        nIds.push(id);
      }
    });
    const iv = {};
    iv['primaryContactNumber'] = investor.primaryContactNumber;
    Object.values(pcs).forEach(pc => {
      iv[`${pc.id}${DOCUMENT_TYPE_ID}`] = pc.documentTypeId;
      pc.userId && (iv[`${pc.id}${USER_ID}`] = pc.userId);
    });
    validData.current = [
      {
        ...controllerProps,
        data: pcs,
        ids: nIds,
        total: nIds.length,
        loading: controllerProps.loading || dtLoading
      },
      iv,
      { ...controllerProps.data }
    ];
    return validData.current;
  }, [
    dtLoaded,
    iLoaded,
    dtLoading,
    documentTypes,
    controllerProps,
    investorId,
    investor
  ]);

  const renderDocumentType = useCallback(
    record =>
      documentTypes[record.documentTypeId]
        ? documentTypes[record.documentTypeId].description
        : 'Unknown Document Type',
    [documentTypes]
  );

  const [dgClasses, selClasses] = useMemo(
    () => [
      { row: classes.row, table: classes.table },
      { input: classes.selectContainer }
    ],
    [classes]
  );

  const onSubmit = useCallback(
    values => {
      const payload = [];
      Object.keys(values).forEach(key => {
        if (key.endsWith(USER_ID)) {
          const id = key.substr(0, key.length - USER_ID.length);
          const fetchedData = validData.current[2][id];
          if (!fetchedData || values[key] !== fetchedData.userId) {
            payload.push({
              investorId: investorId,
              documentTypeId: values[id + DOCUMENT_TYPE_ID],
              type: 'P',
              userId: values[key] || ''
            });
          }
        }
      });
      setSubmitting && setSubmitting(true);
      const promises = [];
      if (payload.length) {
        promises.push(InvestorApi.updatePrimaryContacts(investorId, payload));
      }
      if (investor.primaryContactNumber !== values.primaryContactNumber) {
        investor.primaryContactNumber = values.primaryContactNumber;
        promises.push(InvestorApi.update([investor]));
      }
      Promise.all(promises)
        .then(() => {
          setSubmitting && setSubmitting(false);
          notify('ra.notification.update_primary_contacts_successfull');
          onSubmitEnd && onSubmitEnd();
          handleDialogToggle && handleDialogToggle();
          refresh();
        })
        .catch(notifyServerError);
    },
    [
      investorId,
      investor,
      onSubmitEnd,
      notify,
      notifyServerError,
      handleDialogToggle,
      setSubmitting,
      refresh
    ]
  );

  useEffect(() => {
    if (dtError || cError || iError) {
      notifyServerError(dtError || cError || iError);
    }
  }, [dtError, cError, iError, notifyServerError]);

  return (
    <SimpleFormWithSave
      initialValues={initialValues}
      toolbar={null}
      onSave={onSubmit}
    >
      <ListContextProvider value={newControllerProps}>
        {controllerProps.loading ? (
          <div className={classes.progress}>
            <CircularProgress />
          </div>
        ) : (
          newControllerProps.loaded &&
          !newControllerProps.loading && (
            <ListView
              {...requestProps}
              {...newControllerProps}
              actions={
                <DefaultPrimaryContact
                  contacts={contacts}
                  loading={cLoading || iLoading}
                  showTitle={!!newControllerProps.ids.length}
                />
              }
            >
              {newControllerProps.ids.length ? (
                <Datagrid classes={dgClasses}>
                  <FunctionField
                    label="Document Type"
                    render={renderDocumentType}
                  />
                  {cLoading && !contacts.length ? (
                    <>
                      <LinearProgress />
                    </>
                  ) : (
                    <SelectField
                      source="userId"
                      label="Primary Contact"
                      choices={contacts}
                      classes={selClasses}
                    />
                  )}
                </Datagrid>
              ) : (
                <Typography variant="caption">
                  There are no Document Types to be displayed
                </Typography>
              )}
            </ListView>
          )
        )}
      </ListContextProvider>
      <FormWrapper
        formRef={formRef}
        setDisableSubmit={setDisableSubmit}
        formClassName={classes.noshow}
      />
    </SimpleFormWithSave>
  );
};

const DefaultPrimaryContact = ({ contacts = [], loading, showTitle }) => {
  return (
    <>
      <Grid container direction="column">
        <Grid item>
          <Typography variant="caption">Default Primary Contact</Typography>
        </Grid>
        <Grid item>
          {loading && !contacts.length ? (
            <LinearProgress />
          ) : (
            <SelectInput
              source="primaryContactNumber"
              choices={contacts}
              variant="standard"
              allowEmpty
              label=""
            />
          )}
        </Grid>
        <Grid item />
        {showTitle && (
          <Grid item>
            <Typography variant="caption">
              Primary Contact for Document Type
            </Typography>
          </Grid>
        )}
      </Grid>
    </>
  );
};

const FormWrapper = ({ formRef, setDisableSubmit }) => {
  const form = useForm();
  const formState = useFormState();
  if (formRef && formRef.current !== form) {
    formRef.current = form;
  }
  useEffect(() => {
    // compare formState.values & initialValues
    setDisableSubmit(formState.pristine);
  }, [formState, setDisableSubmit]);
  return null;
};
const SelectField = props => {
  return (
    <SelectInput
      {...props}
      source={`${props.record.id}:userId`}
      id={props.record.id}
      allowEmpty
      options={selectOptions}
      variant="standard"
    />
  );
};

const PrimaryContactsDialog = ({
  investorId,
  onSubmitEnd,
  showDialog,
  handleDialogToggle
}) => {
  const formRef = useRef(null);
  const [disableSubmit, setDisableSubmit] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const classes = useStyles();

  const onSubmit = useCallback(() => {
    formRef.current && formRef.current.submit();
  }, []);

  const onCancel = useCallback(() => {
    formRef.current && formRef.current.reset();
    handleDialogToggle();
  }, [handleDialogToggle]);

  return (
    <Dialog
      fullWidth
      open={!!showDialog}
      aria-label="Manage Primary Contacs"
      maxWidth="lg"
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return null;
        handleDialogToggle();
      }}
    >
      <DialogTitleWithCloseButton onClose={handleDialogToggle}>
        <Typography variant="caption">Manage Primary Contacts</Typography>
      </DialogTitleWithCloseButton>
      <DialogContent classes={{ root: classes.dialog }}>
        <PrimaryContacts
          investorId={investorId}
          onSubmitEnd={onSubmitEnd}
          formRef={formRef}
          setDisableSubmit={setDisableSubmit}
          setSubmitting={setSubmitting}
          handleDialogToggle={handleDialogToggle}
        />
      </DialogContent>
      <DialogActions>
        <SaveButton
          style={{ minWidth: '82px' }}
          saving={submitting}
          handleSubmitWithRedirect={onSubmit}
          label="Save"
          icon={<></>}
          redirect={false}
          disabled={disableSubmit}
        />
        <Button onClick={onCancel} variant="outlined" color="primary">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrimaryContactsDialog;
